import { useLazyQuery, useMutation } from "@apollo/client/react";
import {
  GetMyStudyProgrammes,
  SaveStudyProgrammesForUserInput,
  SaveStudyProgrammesForUserVariables,
} from "__generated__/types";
import { useAuth } from "common/auth/Auth";
import { MutationLoadingOrError } from "common/components/forms/MutationLoadingOrError";
import { SubmitButton } from "common/components/forms/SubmitButton";
import { H4, Paragraph } from "common/components/Typography";
import {
  GET_MY_STUDYPROGRAMMES,
  StudyProgrammesActionTypes,
} from "common/context/MyStudyProgrammesContext";
import { useMyStudyProgrammes } from "common/context/ExtendedStudyProgrammesContext";
import { useMyExamSyllabus } from "common/hooks/useMyExamSyllabus";
import { contentToSelectOption } from "domain/admin/util";
import styles from "domain/front-page/FrontPage.module.css";
import gql from "graphql-tag";
import React, { useEffect } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import ReactSelect from "react-select";

interface FormValues {
  studyProgrammes: { value: string; label: string }[];
}

const SAVE_STUDY_PROGRAMMES_FOR_USER = gql`
  mutation SaveStudyProgrammesForUser(
    $input: SaveStudyProgrammesForUserInput!
  ) {
    saveStudyProgrammesForUser(input: $input) {
      studyProgrammes {
        title
        id
      }
    }
  }
`;

interface SaveStudyProgrammesForUserFormValues {
  studyProgrammes: { value: string; label: string }[];
}

export const SelectStudyProgrammes: React.FC = () => {
  const auth = useAuth();
  const methods = useForm<FormValues>();
  const { state: myStudyProgrammes, dispatch } = useMyStudyProgrammes();

  const [mutate, { error, loading }] = useMutation<
    SaveStudyProgrammesForUserInput,
    SaveStudyProgrammesForUserVariables
  >(SAVE_STUDY_PROGRAMMES_FOR_USER);

  const [refetchStudyProgrammes, { data: studyProgrammesData }] =
    useLazyQuery<GetMyStudyProgrammes>(GET_MY_STUDYPROGRAMMES);

  useEffect(() => {
    if (studyProgrammesData !== undefined) {
      dispatch({
        type: StudyProgrammesActionTypes.SetData,
        payload: {
          myStudyProgrammes: studyProgrammesData.studyProgrammeList,
        },
      });
    }
  }, [studyProgrammesData]);

  const signUpForStudyProgrammes = async (
    input: SaveStudyProgrammesForUserFormValues,
  ) => {
    await mutate({
      variables: {
        input: {
          studyProgrammeIds: input.studyProgrammes.map((e) => e.value),
        },
      },
      refetchQueries: [
        {
          query: GET_MY_STUDYPROGRAMMES,
        },
      ],
      awaitRefetchQueries: true,
    });
  };

  const { refetch: refetchExamSyllabus } = useMyExamSyllabus();

  return (
    <>
      <H4>Hvilket studie går du på?</H4>
      {auth.me?.menu.admin && (
        <Paragraph>
          <em>
            Du er logget inn som admin, men kan likevel velge studieprogram for
            å se hvordan Mitt pensum ser ut for studenter.
          </em>
        </Paragraph>
      )}
      <FormProvider {...methods}>
        <form
          onSubmit={methods.handleSubmit(async (values: FormValues) => {
            await signUpForStudyProgrammes(values);
            await refetchStudyProgrammes();
            refetchExamSyllabus();
          })}
        >
          {myStudyProgrammes.containerStructure.studyProgrammesWithContainers
            .length == 0 ? (
            <div>Laster inn studieprogrammer, vennligst vent...</div>
          ) : (
            <div className={styles.formContainer}>
              <Controller
                name="studyProgrammes"
                control={methods.control}
                render={({ field }) => (
                  <ReactSelect
                    options={myStudyProgrammes.containerStructure.studyProgrammesWithContainers
                      .filter(
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
                        (e) => e.studyProgrammeContainer.status == "PUBLISHED",
                      )
                      .map((e) => ({
                        key: e.studyProgrammeContainer.referencedContentId,
                        value: e.studyProgrammeContainer.referencedContentId,
                        label: e.studyProgrammeContainer.title,
                      }))}
                    noOptionsMessage={() => "Ingen studieprogrammer funnet"}
                    placeholder="Velg studieprogram"
                    isMulti={true}
                    defaultValue={myStudyProgrammes.studyProgrammes.map(
                      contentToSelectOption,
                    )}
                    styles={{
                      option: (provided) => ({
                        ...provided,
                        color: "black",
                      }),
                    }}
                    {...field}
                  />
                )}
              />
              <SubmitButton kind="secondary" loading={loading}>
                Lagre
              </SubmitButton>
            </div>
          )}
          <MutationLoadingOrError loading={loading} error={error} />
        </form>
      </FormProvider>
    </>
  );
};
